import {
  addExpense,
  amountText,
  AmountField,
  convert,
  billUrl,
  BinIcon,
  CameraIcon,
  ConfirmDialog,
  DatePick,
  Dialog,
  formatMoney,
  getLedger,
  getRate,
  InvalidInput,
  Pick,
  removeBill,
  removeExpense,
  settleUp,
  toIso,
  uploadBill,
  type Expense,
  type Ledger,
  type Member,
} from '@trippath/shared';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { day, problemKey } from '../../lib/format';
import { useLive } from '../../app/live';
import { useReadOnly } from '../../app/readOnly';

interface Props {
  trip: string;
  /** Everyone on the trip; a viewer has no part in the costs. */
  members: Member[];
}

/**
 * The split of an expense in words, as an account book says it: none when the one who paid
 * carries it alone (`—`), the one it is for, or `Equally: Kadri, John`.
 */
function splitText(expense: Expense, equally: (names: string) => string): string {
  const [first, ...rest] = expense.shares;
  if (first && rest.length === 0) return first.userId === expense.paidBy ? '—' : first.name;
  return equally(expense.shares.map((s) => s.name).join(', '));
}

/** The money of the country one is in on `on`: its stop's, else the trip's own. */
function moneyOn(ledger: Ledger, on: string | null): string {
  const stay = on === null ? undefined : ledger.stays.find((s) => s.from <= on && on < s.until);
  return stay?.currency ?? ledger.currency;
}

/**
 * The trip's shared costs (the Group mockup): who paid what for whom, every entry with its bill
 * photo as proof, where each person stands, and the payments that settle up.
 */
export function SharedCosts({ trip, members }: Props) {
  // Read again when someone else changes the trip.
  const { version: changed } = useLive();
  const { t, i18n } = useTranslation();
  // A trip read only: its costs said, none added, settled or taken off.
  const locked = useReadOnly();
  const lang = i18n.language;
  const ids = useId();
  const file = useRef<HTMLInputElement>(null);
  const first = useRef<HTMLInputElement>(null);
  const joined = members.filter((m) => m.role !== 'viewer');
  const [ledger, setLedger] = useState<Ledger | null>(null);
  const [failed, setFailed] = useState(false);
  const [adding, setAdding] = useState(false);
  const [dayValue, setDay] = useState<string | null>(toIso(new Date()));
  const [item, setItem] = useState('');
  // The money paid in: picked, else the one of the country the day is spent in.
  const [picked, setPicked] = useState<string | null>(null);
  const [paid, setPaid] = useState<number | null>(null);
  // What the bank charged in the trip's money, when paid in another.
  const [charged, setCharged] = useState<number | null>(null);
  const [paidBy, setPaidBy] = useState<string | null>(null);
  // Whom it is split between: none ticked until said, as no one is assumed to share it.
  const [sharedWith, setSharedWith] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [photoFor, setPhotoFor] = useState<Expense | null>(null);
  const [shown, setShown] = useState<Expense | null>(null);
  const [removing, setRemoving] = useState<Expense | null>(null);
  const [notPhoto, setNotPhoto] = useState(false);

  useEffect(() => {
    getLedger(trip).then(setLedger, () => {
      setFailed(true);
    });
  }, [trip, changed]);

  // The rate of the money paid in, on its day: what it comes to, shown faint where the bank's
  // charge is typed. Kept by the money and day it is for, so an older answer is not used.
  const [rateFor, setRateFor] = useState<{ key: string; perTrip: number | null } | null>(null);
  useEffect(() => {
    if (!adding || ledger === null || dayValue === null) return;
    const paidIn = picked ?? moneyOn(ledger, dayValue);
    if (paidIn === ledger.currency) return;
    void getRate(trip, paidIn, dayValue)
      .catch(() => null)
      .then((found) => {
        setRateFor({ key: `${paidIn} ${dayValue}`, perTrip: found?.perTrip ?? null });
      });
  }, [adding, trip, ledger, picked, dayValue]);

  const done = (job: Promise<Ledger>) => {
    setFailed(false);
    job.then(setLedger, () => {
      setFailed(true);
    });
  };

  if (ledger === null) {
    return failed ? (
      <p className="form-failed" role="alert">
        {t('trips.failed')}
      </p>
    ) : null;
  }

  // Who pays and shares: the members, then who travels along with no account.
  const people = [...joined, ...ledger.fellows];
  const money = (minor: number) => formatMoney({ minor, currency: ledger.currency }, lang);
  const today = toIso(new Date());
  const currency = picked ?? moneyOn(ledger, dayValue);
  const foreign = currency !== ledger.currency;
  // What the book adds up to: what was bought, not debts paid back.
  const total = ledger.expenses
    .filter((expense) => !expense.settlement)
    .reduce((sum, expense) => sum + expense.amount.minor, 0);
  // One alone on the trip pays and carries it all: nobody to pick or share it with.
  const [only] = people.length === 1 ? people : [];
  const payer = only?.userId ?? paidBy ?? ledger.me;
  const split = only === undefined ? sharedWith : [only.userId];
  const perTrip = rateFor?.key === `${currency} ${String(dayValue)}` ? rateFor.perTrip : null;
  const estimate =
    paid !== null && perTrip !== null
      ? convert(paid, currency, ledger.currency, perTrip)
      : null;

  const submit = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    const required = t('problem.required');
    const missing: Record<string, string> = {};
    if (item.trim() === '') missing.item = required;
    if (paid === null) missing.paid = required;
    if (split.length === 0) missing.sharedWith = required;
    if (dayValue === null) missing.day = required;
    setErrors(missing);
    if (paid === null || dayValue === null || Object.keys(missing).length > 0) {
      return;
    }
    addExpense(trip, {
      day: dayValue,
      item,
      paid,
      currency,
      amount: foreign ? charged : null,
      paidBy: payer,
      sharedWith: split,
    }).then(
      (next) => {
        setLedger(next);
        setAdding(false);
        setItem('');
        setPicked(null);
        setPaid(null);
        setCharged(null);
      },
      (error: unknown) => {
        if (error instanceof InvalidInput) {
          setErrors(
            Object.fromEntries(error.fields.map((f) => [f.field, t(problemKey(f.problem))])),
          );
        } else {
          setFailed(true);
        }
      },
    );
  };

  return (
    <section className="panel shared-costs" aria-labelledby={`${ids}-title`}>
      <div className="panel-head">
        <div>
          <h2 id={`${ids}-title`}>{t('costs.title')}</h2>
          <p className="quiet">{t('costs.lead')}</p>
        </div>
        {!locked && (
          <button
            type="button"
            className="button button-primary"
            onClick={() => {
              setErrors({});
              setAdding(true);
            }}
          >
            {t('costs.add')}
          </button>
        )}
      </div>
      {failed && (
        <p className="form-failed" role="alert">
          {t('plan.failed')}
        </p>
      )}

      {notPhoto && (
        <p className="form-failed" role="alert">
          {t('costs.notPhoto')}
        </p>
      )}
      {ledger.expenses.length === 0 ? (
        <p className="quiet">{t('costs.none')}</p>
      ) : (
        <div className="table-box">
          <table className="ledger">
            <thead>
              <tr>
                <th scope="col">{t('costs.day')}</th>
                <th scope="col">{t('costs.item')}</th>
                <th scope="col">{t('costs.paidBy')}</th>
                <th scope="col">{t('costs.split')}</th>
                <th scope="col" className="ledger-amount">
                  {t('costs.amount')}
                </th>
                <th scope="col">{t('costs.bill')}</th>
                <th scope="col" aria-label={t('costs.remove')} />
              </tr>
            </thead>
            <tbody>
              {ledger.expenses.map((expense) => (
                <tr key={expense.id}>
                  <td className="ledger-day">{day(expense.day, lang)}</td>
                  <th scope="row">{expense.settlement ? t('costs.settled') : expense.item}</th>
                  <td>{expense.payer}</td>
                  <td>
                    {expense.settlement
                      ? t('costs.to', { name: expense.shares.map((s) => s.name).join(', ') })
                      : splitText(expense, (names) => t('costs.equally', { names }))}
                  </td>
                  <td className="ledger-amount">
                    {money(expense.amount.minor)}
                    {/* Paid in another money: what the bill said, under what it came to. */}
                    {expense.paid.currency !== ledger.currency && (
                      <small className="ledger-paid">{formatMoney(expense.paid, lang)}</small>
                    )}
                  </td>
                  <td>
                    {/* A bill looked at; on a trip read only, none added. */}
                    {!expense.settlement && (!locked || expense.photo) && (
                      <button
                        type="button"
                        className={
                          expense.photo
                            ? 'icon-button icon-button-small bill-kept'
                            : 'icon-button icon-button-small bill-missing'
                        }
                        aria-label={t(expense.photo ? 'costs.showBill' : 'costs.addBill', {
                          item: expense.item,
                        })}
                        title={t(expense.photo ? 'costs.viewBill' : 'costs.addPhoto')}
                        onClick={() => {
                          if (expense.photo) {
                            setShown(expense);
                          } else {
                            setPhotoFor(expense);
                            file.current?.click();
                          }
                        }}
                      >
                        <CameraIcon size={14} />
                      </button>
                    )}
                  </td>
                  <td>
                    {!locked && (
                      <button
                        type="button"
                        className="icon-button icon-button-small"
                        aria-label={t('costs.removeOne', { item: expense.item })}
                        title={t('costs.remove')}
                        onClick={() => {
                          setRemoving(expense);
                        }}
                      >
                        <BinIcon />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row" colSpan={4}>
                  {t('costs.total')}
                </th>
                <td className="ledger-amount">{money(total)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      <input
        ref={file}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        tabIndex={-1}
        aria-hidden="true"
        data-testid="bill-input"
        onChange={(event) => {
          const chosen = event.target.files?.[0];
          if (chosen && photoFor) {
            setFailed(false);
            setNotPhoto(false);
            uploadBill(trip, photoFor.id, chosen).then(setLedger, (error: unknown) => {
              if (error instanceof InvalidInput) setNotPhoto(true);
              else setFailed(true);
            });
          }
          event.target.value = '';
        }}
      />

      {ledger.expenses.length > 0 && (
        <>
          {/* Where each one stands: paid, their share, and the difference — above nothing they
              get money back, below it they owe. */}
          <h3 className="ledger-head">{t('costs.balances')}</h3>
          <div className="table-box">
            <table className="ledger">
              <thead>
                <tr>
                  <th scope="col">{t('costs.person')}</th>
                  <th scope="col" className="ledger-amount">
                    {t('costs.paidSum')}
                  </th>
                  <th scope="col" className="ledger-amount">
                    {t('costs.share')}
                  </th>
                  <th scope="col" className="ledger-amount">
                    {t('costs.net')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {ledger.balances.map((balance) => (
                  <tr key={balance.userId}>
                    <th scope="row">{balance.name}</th>
                    <td className="ledger-amount">{money(balance.paid.minor)}</td>
                    <td className="ledger-amount">{money(balance.share.minor)}</td>
                    <td
                      className={
                        balance.net.minor < 0
                          ? 'ledger-amount ledger-owes'
                          : balance.net.minor > 0
                            ? 'ledger-amount ledger-owed'
                            : 'ledger-amount'
                      }
                    >
                      {balance.net.minor > 0 ? '+' : ''}
                      {money(balance.net.minor)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <h3 className="ledger-head">{t('costs.settleTitle')}</h3>
          {ledger.transfers.length === 0 ? (
            <p className="settled-note">{t('costs.square')}</p>
          ) : (
            <ul className="transfers">
              {ledger.transfers.map((transfer) => (
                <li key={`${transfer.from.userId}-${transfer.to.userId}`}>
                  <span>
                    {t('costs.pays', {
                      from: transfer.from.name,
                      to: transfer.to.name,
                      amount: money(transfer.amount.minor),
                    })}
                  </span>
                  {!locked && (
                    <button
                      type="button"
                      className="button"
                      onClick={() => {
                        done(
                          settleUp(trip, {
                            from: transfer.from.userId,
                            to: transfer.to.userId,
                            amount: transfer.amount.minor,
                            day: today,
                          }),
                        );
                      }}
                    >
                      {t('costs.markSettled')}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      <p className="quiet">
        {ledger.missingPhotos === 0
          ? ledger.expenses.length > 0 && t('costs.allPhotos')
          : t('costs.missing', { count: ledger.missingPhotos })}
      </p>

      <Dialog
        open={adding}
        onClose={() => {
          setAdding(false);
        }}
        title={t('costs.newTitle')}
        initialFocus={first}
      >
        <form className="form" onSubmit={submit} noValidate>
          <DatePick
            label={t('costs.day')}
            value={dayValue}
            onChange={(next) => {
              setDay(next);
              // The money follows the day, until one is picked: a new one, a new amount.
              if (picked === null && moneyOn(ledger, next) !== currency) setPaid(null);
            }}
            required
          />
          {errors.day !== undefined && <p className="field-error">{errors.day}</p>}
          <div className="field">
            <label className="field-label" htmlFor={`${ids}-item`} data-required>
              {t('costs.item')}
            </label>
            <input
              id={`${ids}-item`}
              ref={first}
              className="text-input"
              type="text"
              placeholder={t('costs.itemHint')}
              value={item}
              aria-invalid={errors.item !== undefined}
              onChange={(event) => {
                setItem(event.target.value);
              }}
            />
            {errors.item !== undefined && <p className="field-error">{errors.item}</p>}
          </div>
          {/* What the bill says, in its money; beside it, what it really came to in the
              trip's: the bank's charge, the cash it took — by the day's rate unless said. */}
          <div className="form-row">
            <AmountField
              key={currency}
              label={t('costs.amount')}
              required
              value={paid}
              onChange={setPaid}
              currency={currency}
              // The trip's own money and its countries' first, then every other.
              currencies={[
                ...new Set([
                  currency,
                  ledger.currency,
                  ...ledger.stays
                    .map((stay) => stay.currency)
                    .filter((code): code is string => code !== null),
                  ...ledger.currencies,
                ]),
              ]}
              onCurrency={(code) => {
                setPicked(code);
                setPaid(null);
                setCharged(null);
              }}
              error={errors.paid}
            />
            {foreign && (
              <AmountField
                key={`charged-${currency}`}
                label={t('costs.charged')}
                value={charged}
                onChange={setCharged}
                currency={ledger.currency}
                placeholder={
                  estimate === null ? undefined : amountText(estimate, ledger.currency)
                }
                error={errors.amount}
              />
            )}
          </div>
          {foreign && (
            <p className="field-hint">
              {perTrip === null ? t('costs.noRate') : t('costs.chargedHint')}
            </p>
          )}
          {only === undefined && (
            <>
              <Pick
                label={t('costs.paidBy')}
                required
                options={people.map((m) => ({ value: m.userId, label: m.name }))}
                value={payer}
                searchable={false}
                onChange={setPaidBy}
              />
              <fieldset className="split-with">
                <legend className="field-label" data-required>
                  {t('costs.sharedWith')}
                </legend>
                {people.map((member) => (
                  <label key={member.userId} className="check">
                    <input
                      type="checkbox"
                      checked={sharedWith.includes(member.userId)}
                      onChange={(event) => {
                        setSharedWith((was) =>
                          event.target.checked
                            ? [...was, member.userId]
                            : was.filter((user) => user !== member.userId),
                        );
                      }}
                    />
                    {member.name}
                  </label>
                ))}
                {errors.sharedWith !== undefined && (
                  <p className="field-error">{errors.sharedWith}</p>
                )}
              </fieldset>
            </>
          )}
          {failed && (
            <p className="form-failed" role="alert">
              {t('plan.failed')}
            </p>
          )}
          <div className="dialog-actions">
            <button
              type="button"
              className="button"
              onClick={() => {
                setAdding(false);
              }}
            >
              {t('dialog.cancel')}
            </button>
            <button type="submit" className="button button-primary">
              {t('costs.save')}
            </button>
          </div>
        </form>
      </Dialog>

      {shown && (
        <Dialog
          open
          onClose={() => {
            setShown(null);
          }}
          title={t('costs.billOf', { item: shown.item })}
        >
          <img
            className="bill-photo"
            src={billUrl(trip, shown.id)}
            alt={t('costs.billAlt', { item: shown.item })}
          />
          <p className="quiet">{t('costs.billKept', { name: shown.payer })}</p>
          <div className="dialog-actions">
            <button
              type="button"
              className="button button-quiet dialog-action-apart"
              onClick={() => {
                setShown(null);
                done(removeBill(trip, shown.id));
              }}
            >
              {t('costs.deleteBill')}
            </button>
          </div>
        </Dialog>
      )}

      {removing && (
        <ConfirmDialog
          open
          title={t('costs.removeQuestion', { item: removing.item })}
          body={t('costs.removeBody')}
          confirmLabel={t('costs.removeConfirm')}
          onCancel={() => {
            setRemoving(null);
          }}
          onConfirm={() => {
            setRemoving(null);
            done(removeExpense(trip, removing.id));
          }}
        />
      )}
    </section>
  );
}
